import * as React from 'react';
import { ITableColumn } from './index';

// --- Helper Interfaces ---

interface ISortConfig {
    field: string | null;
    isDescending: boolean | null;
}

interface ISortableTableColumn extends ITableColumn {
    sortable?: boolean;
    mapping: string;
}

interface ITableHeaderProps {
    isLoading?: boolean;
    columns: ISortableTableColumn[];
    onSort?: (field: string) => void;
    sortConfig?: ISortConfig;
}

// --- Local Styles for the TableHeader ---
// This CSS is scoped using class names and will only affect this component.
const tableHeaderStyles = `
  /* This class is ONLY added to sortable headers */
  .sortable-header {
    /* Make the cursor a pointer to show it's clickable */
    cursor: pointer;
    user-select: none; /* Prevents text selection on click */
    transition: background-color 0.2s ease;
  }

  /* This :hover pseudo-class will ONLY apply to sortable headers */
  .sortable-header:hover {
    background-color: #f1f1f1; /* A light grey hover effect */
    color: #212529;
  }
  
  /* Style for the small up/down arrow */
  .sort-indicator {
    display: inline-block;
    margin-left: 6px;
    font-size: 0.8em;
  }
`;

// --- The TableHeader Component ---
const TableHeader: React.StatelessComponent<ITableHeaderProps> = ({ columns, onSort, sortConfig }) => {
    return (
        <>
            {/* 
              This <style> tag injects the local CSS. It will not affect
              any other component in your application.
            */}
            <style>{tableHeaderStyles}</style>

            <thead>
                <tr>
                    {columns.map((column) => {
                        // A column is sortable only if `onSort` is provided and the column is marked `sortable`
                        const isSortable = !!onSort && column.sortable;

                        // Check if this is the column that is currently being sorted
                        const isCurrentlySorted = isSortable && sortConfig?.field === column.mapping;

                        // The className will be 'sortable-header' for clickable columns,
                        // and 'sortable-header sorted' for the active one.
                        const classNames = [
                            isSortable ? 'sortable-header' : '',
                            isCurrentlySorted ? 'sorted' : ''
                        ].filter(Boolean).join(' ');

                        return (
                            <th
                                key={column.mapping || column.name}
                                className={classNames}
                                // The onClick is only active if the column is sortable
                                onClick={() => isSortable && onSort(column.mapping)}
                                // We still use an inline style for the font-weight as it's a dynamic boolean check
                                style={{ fontWeight: isCurrentlySorted ? 'bold' : 'normal' }}
                            >
                                {column.name}

                                {/* The sort indicator (▲ or ▼) only shows on the active column */}
                                {isCurrentlySorted && (
                                    <span className="sort-indicator">
                                        {sortConfig?.isDescending ? ' ▼' : ' ▲'}
                                    </span>
                                )}
                            </th>
                        );
                    })}
                </tr>
            </thead>
        </>
    );
};

export default TableHeader;


// WEBPACK FOOTER //
// ./src/components/common/table/TableHeader.tsx